import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Select, Form, message, Input } from 'antd';
import styles from './s.module.less';
import { updateClosedFunnelFilterBatch, updateClosedFunnelFilter } from 'api/operation/closedFunnel';
import { EClosedFunnelFilterType, IClosedFunnelFilter } from 'types/operation';
import useAllMedications from 'hooks/useAllMedications';

export const TreatmentOptions = [
    { label: 'Medication management', value: 'prescribers' },
    { label: 'Therapy and Counseling', value: 'therapist' },
];

export const GrowthTierOptions = [
    { label: 'Starter providers', value: 'Starter' },
    { label: 'Growth providers', value: 'Growth' },
    { label: 'Pro-listings providers', value: 'Scaling' },
];

export const TitleText = {
    [EClosedFunnelFilterType.TREATMENT]: 'Filter by treatment',
    [EClosedFunnelFilterType.NON_PRESCRIBABLE_MEDICATION]: 'Filter by treatment',
    [EClosedFunnelFilterType.GROWTH_TIER]: 'Filter by growth target ',
};

export const LabelText = {
    [EClosedFunnelFilterType.GROWTH_TIER]: 'Provider type',
};

interface MatchingProviderEditModalProps {
    open: boolean;
    filters: IClosedFunnelFilter[]; // 改为接收filters数组
    onSuccess: () => void;
    onCancel: () => void;
}

const MatchingProviderEditModal: React.FC<MatchingProviderEditModalProps> = ({
    open,
    filters,
    onSuccess,
    onCancel,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const filterType = useMemo(() => filters[0].filterType, [filters]); // for 单独 filter

    // 使用hook获取medication数据
    const { medications, loading: medicationsLoading, error: medicationsError } = useAllMedications();

    // 将medication字符串数组转换为选项格式
    const medicationOptions = medications.map((medication) => ({
        label: medication,
        value: medication,
    }));

    // 判断是否是治疗类型的filters
    const isTreatmentType = filters.every((filter) =>
        filter.filterType === 'TREATMENT' || filter.filterType === 'NON_PRESCRIBABLE_MEDICATION',
    );

    // 处理medication获取错误
    useEffect(() => {
        if (medicationsError) {
            console.error('Failed to load medications:', medicationsError);
        }
    }, [medicationsError]);

    // 检查是否选择了药物管理治疗方式
    const isMedicationManagementSelected = (treatments: string[]) => {
        return treatments?.some((treatmentValue) =>
            TreatmentOptions.find((option) =>
                option.value === treatmentValue &&
                option.label?.toLowerCase().includes('medication management'),
            ),
        );
    };

    useEffect(() => {
        if (filters && filters.length > 0) {
            if (isTreatmentType) {
                // 治疗类型的filters - 合并数据处理
                const allTreatments: string[] = [];
                const allExcludedMedications: string[] = [];

                // 遍历所有filters，合并数据用于编辑
                filters.forEach((filter) => {
                    // 如果 filterValues 为空字符串，跳过处理
                    if (!filter.filterValues || filter.filterValues.trim() === '') {
                        return;
                    }

                    try {
                        // 尝试解析JSON格式的filterValues
                        const filterData = JSON.parse(filter.filterValues);

                        // 合并treatments
                        if (filterData.treatments && Array.isArray(filterData.treatments)) {
                            allTreatments.push(...filterData.treatments);
                        }

                        // 合并excludedMedications
                        if (filterData.excludedMedications && Array.isArray(filterData.excludedMedications)) {
                            allExcludedMedications.push(...filterData.excludedMedications);
                        }
                    } catch (error) {
                        // 如果解析失败，按逗号分隔字符串处理（向后兼容）
                        if (filter.filterType === 'TREATMENT') {
                            const treatments = filter.filterValues.split(',').filter((t) => t.trim());
                            allTreatments.push(...treatments);
                        } else if (filter.filterType === 'NON_PRESCRIBABLE_MEDICATION') {
                            // 处理NON_PRESCRIBABLE_MEDICATION类型的逗号分隔字符串
                            const medicationValues = filter.filterValues.split(',').filter((m) => m.trim());
                            allExcludedMedications.push(...medicationValues);
                        }
                    }
                });

                // 去重
                const uniqueTreatments = Array.from(new Set(allTreatments));
                const uniqueExcludedMedications = Array.from(new Set(allExcludedMedications));

                // 先设置 excludedMedications，再设置 filterValues，避免 onChange 清空数据
                form.setFieldsValue({
                    excludedMedications: uniqueExcludedMedications,
                    filterValues: uniqueTreatments,
                });
            } else {
                // 其他类型的filter - 显示原始值编辑
                const filter = filters[0];
                if (filter && filter.filterValues) {
                    if (filter.filterType === EClosedFunnelFilterType.GROWTH_TIER) {
                        form.setFieldsValue({
                            otherFilterValues: filter.filterValues.split(',').filter((v) => v.trim()),
                        });
                    } else {
                        form.setFieldsValue({
                            otherFilterValues: filter.filterValues,
                        });
                    }
                } else {
                    form.setFieldsValue({
                        otherFilterValues: filterType === EClosedFunnelFilterType.GROWTH_TIER ? [] : '',
                    });
                }
            }
        } else {
            form.setFieldsValue({
                filterValues: [],
                excludedMedications: [],
                otherFilterValues: filterType === EClosedFunnelFilterType.GROWTH_TIER ? [] : '',
            });
        }
    }, [form, filters, isTreatmentType, filterType]);

    // 监听治疗选择变化
    const handleTreatmentChange = (values: string[]) => {
        // 如果没有选择Medication management，清空药物排除选项
        if (!isMedicationManagementSelected(values || [])) {
            form.setFieldsValue({ excludedMedications: [] });
        }
    };

    const handleSave = () => {
        if (filters && filters.length > 0) {
            form.validateFields().then(async (values) => {
                setLoading(true);
                try {
                    if (isTreatmentType) {
                        // 治疗类型 - 使用批量更新
                        const treatmentValues = values.filterValues?.filter((v: string) => v) || [];
                        const medicationValues = values.excludedMedications?.filter((v: string) => v) || [];

                        // 准备批量更新的数据
                        const batchUpdateData = filters.map((filter) => {
                            let newFilterValues: string;

                            if (filter.filterType === 'TREATMENT') {
                                // 对于TREATMENT类型，更新treatments
                                newFilterValues = treatmentValues.join(',');
                            } else if (filter.filterType === 'NON_PRESCRIBABLE_MEDICATION') {
                                // 对于NON_PRESCRIBABLE_MEDICATION类型，更新excludedMedications
                                newFilterValues = medicationValues.join(',');
                            } else {
                                // 保持原有数据不变
                                newFilterValues = filter.filterValues;
                            }

                            return {
                                id: filter.id,
                                filterValues: newFilterValues,
                            };
                        });

                        const res = await updateClosedFunnelFilterBatch(batchUpdateData);
                        if (res && !res.error) {
                            onSuccess?.();
                        } else {
                            message.error(res?.message || 'Update failed. Please try again.');
                        }
                    } else {
                        // 其他类型 - 使用单个更新
                        const filter = filters[0];
                        const newFilterValues = Array.isArray(values.otherFilterValues) ? values.otherFilterValues.join(',') : values.otherFilterValues || '';

                        const res = await updateClosedFunnelFilter(filter.id, newFilterValues);
                        if (res && !res.error) {
                            onSuccess?.();
                        } else {
                            message.error(res?.message || 'Update failed');
                        }
                    }
                } catch (error) {
                    console.error(error);
                    message.error('Update failed');
                } finally {
                    setLoading(false);
                }
            });
        }
    };

    // 获取模态框标题
    const getModalTitle = () => {
        if (isTreatmentType) {
            return 'Filter by treatment';
        } else {
            const filter = filters[0];
            const filterTypeName = filter.filterType.replace(/_/g, ' ').toLowerCase()
                .replace(/\b\w/g, (l) => l.toUpperCase());
            return TitleText[filter.filterType] || `Filter by ${filterTypeName}`;
        }
    };

    return (
        <Modal
            open={open}
            footer={null}
            closable={false}
            className={styles.modal}
            width={680}
            centered
            destroyOnClose
        >
            <div className={styles.header}>
                <span className={styles.title}>{getModalTitle()}</span>
                <div className={styles.headerBtns}>
                    <Button className={styles.cancelBtn} onClick={onCancel}>Cancel</Button>
                    <Button className={styles.saveBtn} type="primary" onClick={handleSave} loading={loading}>Save changes</Button>
                </div>
            </div>
            <div className={styles.requiredTip}><span className={styles.red}>*</span> Indicates required fields</div>
            <Form
                form={form}
                layout="vertical"
            >
                {isTreatmentType ? (
                    // 治疗类型的编辑界面
                    <>
                        <Form.Item
                            label={<span className={styles.label}>Treatment</span>}
                            name="filterValues"
                        // rules={[{ required: true, message: 'Please select at least one treatment' }]}
                        >
                            <Select
                                mode="multiple"
                                allowClear
                                options={TreatmentOptions.filter((option) => option.label && option.value)}
                                className={styles.select}
                                placeholder="Select treatment"
                                onChange={handleTreatmentChange}
                            />
                        </Form.Item>
                        <Form.Item
                            label={<span className={styles.label}>Exclude providers who don’t prescribe</span>}
                            name="excludedMedications"
                        >
                            <Select
                                mode="multiple"
                                allowClear
                                options={medicationOptions.filter((option) => option.label && option.value)}
                                className={styles.select}
                                placeholder="Select a medication to exclude providers"
                                // disabled={!isMedicationManagementSelected(selectedTreatments)}
                                loading={medicationsLoading}
                            />
                        </Form.Item>
                    </>
                ) : (
                    // 其他类型的编辑界面
                    <Form.Item
                        label={<span className={styles.label}>{LabelText[filterType as keyof typeof LabelText]}</span>}
                        name="otherFilterValues"
                        rules={[{ required: true, message: 'Please enter filter values' }]}
                    >
                        {filterType === EClosedFunnelFilterType.GROWTH_TIER ? (
                            <Select
                                mode="multiple"
                                allowClear
                                options={GrowthTierOptions.filter((option) => option.label && option.value)}
                                className={styles.select}
                                placeholder="Select provider type"
                            />
                        ) : (
                            <Input.TextArea
                                className={styles.textArea}
                                placeholder="Enter filter values"
                                rows={4}
                            />
                        )}
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
};

export default MatchingProviderEditModal;
